import {
  Condition,
  DiagnosticReport,
  Encounter,
  Immunization,
  MedicationAdministration,
  MedicationRequest,
  Observation,
  Procedure,
  Resource,
} from "@medplum/fhirtypes";

export type EvidenceResourceDisplay = {
  colorPalette: string;
  name: string;
  code?: string;
  date?: string;
  status?: string;
  value?: string;
  interpretation?: string;
  interpretationColor?: string;
  components?: Array<{ name: string; value: string }>;
};

export function getEvidenceResourceDisplay(resource: Resource): EvidenceResourceDisplay {
  switch (resource.resourceType) {
    case "Observation":
      return getObservationDisplay(resource);
    case "Condition":
      return getConditionDisplay(resource);
    case "Procedure":
      return getProcedureDisplay(resource);
    case "MedicationRequest":
      return getMedicationRequestDisplay(resource);
    case "MedicationAdministration":
      return getMedicationAdministrationDisplay(resource);
    case "DiagnosticReport":
      return getDiagnosticReportDisplay(resource);
    case "Encounter":
      return getEncounterDisplay(resource);
    case "Immunization":
      return getImmunizationDisplay(resource);
    default:
      return { colorPalette: "gray", name: resource.resourceType };
  }
}

export function getEvidenceResourceDate(resource: Resource): string | undefined {
  switch (resource.resourceType) {
    case "Observation":
      return resource.effectiveDateTime ?? resource.effectivePeriod?.start;
    case "Condition":
      return resource.onsetDateTime ?? resource.recordedDate;
    case "Procedure":
      return resource.performedDateTime ?? resource.performedPeriod?.start;
    case "MedicationRequest":
      return resource.authoredOn;
    case "MedicationAdministration":
      return resource.effectiveDateTime ?? resource.effectivePeriod?.start;
    case "DiagnosticReport":
      return resource.effectiveDateTime ?? resource.effectivePeriod?.start;
    case "Encounter":
      return resource.period?.start;
    case "Immunization":
      return resource.occurrenceDateTime;
    default:
      return undefined;
  }
}

function formatDate(dateStr: string | undefined): string | undefined {
  if (!dateStr) return undefined;
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return undefined;
  }
}

function getCodeDisplay(
  code: Observation["code"] | undefined
): { name: string; code?: string } | undefined {
  if (!code) return undefined;
  const name = code.text ?? code.coding?.[0]?.display ?? undefined;
  const codeStr = code.coding?.[0]?.code ?? undefined;
  return name ? { name, code: codeStr } : undefined;
}

function getInterpretationColor(lower: string): string {
  if (lower.includes("high") || lower.includes("critical")) {
    return "red";
  }
  if (lower.includes("low")) {
    return "orange";
  }
  if (lower.includes("normal")) {
    return "green";
  }
  return "blue";
}

function getInterpretationDisplay(
  interpretation: Observation["interpretation"]
): { text: string; color: string } | undefined {
  const text = interpretation?.[0]?.text ?? interpretation?.[0]?.coding?.[0]?.display ?? undefined;
  if (!text) return undefined;
  const lower = text.toLowerCase();
  return { text, color: getInterpretationColor(lower) };
}

function getObservationComponents(
  components: Observation["component"]
): Array<{ name: string; value: string }> {
  return (
    components
      ?.map(comp => {
        const name = comp.code?.text ?? comp.code?.coding?.[0]?.display ?? "Component";
        let value = "";
        if (comp.valueQuantity) {
          const unit = comp.valueQuantity.unit ?? "";
          value = `${comp.valueQuantity.value} ${unit}`.trim();
        } else if (comp.valueString) {
          value = comp.valueString;
        }
        return value ? { name, value } : undefined;
      })
      .filter((c): c is { name: string; value: string } => c !== undefined) ?? []
  );
}

function getObservationDisplay(obs: Observation): EvidenceResourceDisplay {
  const codeDisplay = getCodeDisplay(obs.code);
  let value: string | undefined;
  if (obs.valueQuantity) {
    const unit = obs.valueQuantity.unit ?? "";
    value = `${obs.valueQuantity.value} ${unit}`.trim();
  } else if (obs.valueString) {
    value = obs.valueString;
  } else if (obs.valueCodeableConcept) {
    value = obs.valueCodeableConcept.text ?? obs.valueCodeableConcept.coding?.[0]?.display;
  } else if (typeof obs.valueBoolean === "boolean") {
    value = obs.valueBoolean ? "Yes" : "No";
  } else if (typeof obs.valueInteger === "number") {
    value = obs.valueInteger.toString();
  }

  const interp = getInterpretationDisplay(obs.interpretation);

  return {
    colorPalette: "blue",
    name: codeDisplay?.name ?? "Observation",
    code: codeDisplay?.code,
    date: formatDate(obs.effectiveDateTime ?? obs.effectivePeriod?.start),
    status: obs.status,
    value,
    interpretation: interp?.text,
    interpretationColor: interp?.color,
    components: getObservationComponents(obs.component),
  };
}

function getConditionDisplay(condition: Condition): EvidenceResourceDisplay {
  const codeDisplay = getCodeDisplay(condition.code);
  return {
    colorPalette: "orange",
    name: codeDisplay?.name ?? "Condition",
    code: codeDisplay?.code,
    date: formatDate(condition.onsetDateTime ?? condition.recordedDate),
    status:
      condition.clinicalStatus?.coding?.[0]?.display ?? condition.clinicalStatus?.coding?.[0]?.code,
  };
}

function getProcedureDisplay(procedure: Procedure): EvidenceResourceDisplay {
  const codeDisplay = getCodeDisplay(procedure.code);
  const performedDate = procedure.performedDateTime ?? procedure.performedPeriod?.start;
  return {
    colorPalette: "purple",
    name: codeDisplay?.name ?? "Procedure",
    code: codeDisplay?.code,
    date: formatDate(performedDate),
    status: procedure.status,
  };
}

function getMedicationRequestDisplay(med: MedicationRequest): EvidenceResourceDisplay {
  const medCode = med.medicationCodeableConcept;
  return {
    colorPalette: "green",
    name: medCode?.text ?? medCode?.coding?.[0]?.display ?? "MedicationRequest",
    code: medCode?.coding?.[0]?.code,
    date: formatDate(med.authoredOn),
    status: med.status,
  };
}

function getMedicationAdministrationDisplay(
  med: MedicationAdministration
): EvidenceResourceDisplay {
  const medCode = med.medicationCodeableConcept;
  const date = med.effectiveDateTime ?? med.effectivePeriod?.start;
  return {
    colorPalette: "green",
    name: medCode?.text ?? medCode?.coding?.[0]?.display ?? "MedicationAdministration",
    code: medCode?.coding?.[0]?.code,
    date: formatDate(date),
    status: med.status,
  };
}

function getDiagnosticReportDisplay(report: DiagnosticReport): EvidenceResourceDisplay {
  const codeDisplay = getCodeDisplay(report.code);
  const date = report.effectiveDateTime ?? report.effectivePeriod?.start;
  return {
    colorPalette: "cyan",
    name: codeDisplay?.name ?? "DiagnosticReport",
    code: codeDisplay?.code,
    date: formatDate(date),
    status: report.status,
  };
}

function getEncounterDisplay(encounter: Encounter): EvidenceResourceDisplay {
  return {
    colorPalette: "pink",
    name: encounter.type?.[0]?.text ?? encounter.type?.[0]?.coding?.[0]?.display ?? "Encounter",
    date: formatDate(encounter.period?.start),
    status: encounter.status,
  };
}

function getImmunizationDisplay(immunization: Immunization): EvidenceResourceDisplay {
  return {
    colorPalette: "teal",
    name:
      immunization.vaccineCode?.text ??
      immunization.vaccineCode?.coding?.[0]?.display ??
      "Immunization",
    code: immunization.vaccineCode?.coding?.[0]?.code,
    date: formatDate(immunization.occurrenceDateTime),
    status: immunization.status,
  };
}
